import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, Grid, List, Filter } from 'lucide-react';
import { motion } from 'framer-motion';
import { Category } from '@/services/productsService';
import productsService from '@/services/productsService';
import CategoryCard from './CategoryCard';
import Input from '@/components/ui/Input';
import Button from '@/components/ui/Button';
import { toast } from 'react-hot-toast';
import { cn, debounce } from '@/utils/utils';

const CategoriesPage: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [categories, setCategories] = useState<Category[]>([]);
    const [filteredCategories, setFilteredCategories] = useState<Category[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
    const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
    const [selectedParent, setSelectedParent] = useState<number | null>(null);

    // Cargar categorías al montar el componente
    useEffect(() => {
        loadCategories();
    }, []);

    // Filtrar categorías cuando cambie la búsqueda o filtros
    useEffect(() => {
        filterCategories();
    }, [searchQuery, selectedParent, categories]);

    const loadCategories = async () => {
        try {
            setIsLoading(true);
            const data = await productsService.getCategories(false, false); // Jerárquico, solo activas
            setCategories(data);
            console.log('Categories loaded:', data);
        } catch (error) {
            console.error('Error loading categories:', error);
            toast.error('Error al cargar las categorías');
        } finally {
            setIsLoading(false);
        }
    };

    const filterCategories = () => {
        let filtered = [...categories];

        // Filtrar por texto de búsqueda
        if (searchQuery) {
            filtered = filtered.filter(category =>
                category.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                category.description?.toLowerCase().includes(searchQuery.toLowerCase())
            );
        }

        // Filtrar por categoría padre
        if (selectedParent !== null) {
            filtered = filtered.filter(category => category.parent_id === selectedParent);
        }

        setFilteredCategories(filtered);
    };

    // Función de búsqueda con debounce
    const debouncedSearch = debounce((query: string) => {
        setSearchQuery(query);
        // Actualizar URL
        if (query) {
            setSearchParams({ q: query });
        } else {
            setSearchParams({});
        }
    }, 500);

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        debouncedSearch(e.target.value);
    };

    // Obtener categorías padre (nivel superior)
    const parentCategories = categories.filter(cat => !cat.parent_id);

    // Skeleton loader
    const CategorySkeleton = () => (
        <div className="bg-white rounded-lg border border-gray-200 p-4 animate-pulse">
            <div className="flex flex-col items-center text-center">
                <div className="w-16 h-16 bg-gray-200 rounded-full mb-3" />
                <div className="h-4 bg-gray-200 rounded w-24 mb-2" />
                <div className="h-3 bg-gray-200 rounded w-16" />
            </div>
        </div>
    );

    if (isLoading) {
        return (
            <div className="min-h-screen bg-gray-50 py-8">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    {/* Header skeleton */}
                    <div className="mb-8">
                        <div className="h-8 bg-gray-200 rounded w-64 mb-4" />
                        <div className="h-4 bg-gray-200 rounded w-96" />
                    </div>

                    {/* Filters skeleton */}
                    <div className="mb-8 flex gap-4">
                        <div className="h-10 bg-gray-200 rounded flex-1 max-w-md" />
                        <div className="h-10 bg-gray-200 rounded w-32" />
                        <div className="h-10 bg-gray-200 rounded w-32" />
                    </div>

                    {/* Categories skeleton */}
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-6">
                        {[...Array(12)].map((_, i) => (
                            <CategorySkeleton key={i} />
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="mb-8">
                    <motion.h1
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-3xl font-bold text-gray-900 mb-4"
                    >
                        Todas las categorías
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="text-gray-600"
                    >
                        Explora nuestra amplia selección de categorías y encuentra exactamente lo que buscas.
                    </motion.p>
                </div>

                {/* Filtros y búsqueda */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="mb-8 flex flex-col sm:flex-row gap-4 items-center justify-between"
                >
                    {/* Búsqueda */}
                    <div className="flex-1 max-w-md">
                        <Input
                            type="text"
                            placeholder="Buscar categorías..."
                            defaultValue={searchQuery}
                            onChange={handleSearchChange}
                            leftIcon={<Search className="w-5 h-5" />}
                            fullWidth
                        />
                    </div>

                    <div className="flex gap-4 items-center">
                        {/* Filtro por categoría padre */}
                        <select
                            value={selectedParent || ''}
                            onChange={(e) => setSelectedParent(e.target.value ? Number(e.target.value) : null)}
                            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                        >
                            <option value="">Todas las categorías</option>
                            {parentCategories.map(category => (
                                <option key={category.id} value={category.id}>
                                    {category.name}
                                </option>
                            ))}
                        </select>

                        {/* Botones de vista */}
                        <div className="flex border border-gray-300 rounded-md">
                            <button
                                onClick={() => setViewMode('grid')}
                                className={cn(
                                    'p-2 border-r border-gray-300',
                                    viewMode === 'grid' ? 'bg-primary-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                                )}
                            >
                                <Grid className="w-5 h-5" />
                            </button>
                            <button
                                onClick={() => setViewMode('list')}
                                className={cn(
                                    'p-2',
                                    viewMode === 'list' ? 'bg-primary-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                                )}
                            >
                                <List className="w-5 h-5" />
                            </button>
                        </div>
                    </div>
                </motion.div>

                {/* Estadísticas */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="mb-8 text-sm text-gray-600"
                >
                    Mostrando {filteredCategories.length} de {categories.length} categorías
                    {searchQuery && ` para "${searchQuery}"`}
                </motion.div>

                {/* Categorías */}
                {filteredCategories.length > 0 ? (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.4 }}
                        className={cn(
                            'grid gap-6',
                            viewMode === 'grid'
                                ? 'grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6'
                                : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
                        )}
                    >
                        {filteredCategories.map((category, index) => (
                            <CategoryCard
                                key={category.id}
                                category={category}
                                variant={viewMode === 'list' ? 'large' : 'default'}
                                showProductCount={true}
                                showDescription={viewMode === 'list'}
                                index={index}
                            />
                        ))}
                    </motion.div>
                ) : (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.4 }}
                        className="text-center py-12"
                    >
                        <div className="text-gray-400 mb-4">
                            <Filter className="w-16 h-16 mx-auto" />
                        </div>
                        <h3 className="text-lg font-medium text-gray-900 mb-2">
                            No se encontraron categorías
                        </h3>
                        <p className="text-gray-600 mb-6">
                            {searchQuery
                                ? `No hay categorías que coincidan con "${searchQuery}"`
                                : 'No hay categorías disponibles en este momento'
                            }
                        </p>
                        {searchQuery && (
                            <Button
                                variant="outline"
                                onClick={() => {
                                    setSearchQuery('');
                                    setSearchParams({});
                                }}
                            >
                                Limpiar búsqueda
                            </Button>
                        )}
                    </motion.div>
                )}
            </div>
        </div>
    );
};

export default CategoriesPage;